import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { CheckCircle2, Zap } from 'lucide-react';
import { DiscoverSettings } from '../../services/DiscoverService';

interface TechSectionProps {
    settings: DiscoverSettings;
}

const TechSection: React.FC<TechSectionProps> = ({ settings }) => {
    const containerRef = useRef<HTMLElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"]
    });

    // Tesla-style parallax and opacity effects based on scroll
    const yTransform = useTransform(scrollYProgress, [0, 1], [100, -100]);
    const opacityTransform = useTransform(scrollYProgress, [0, 0.5, 1], [0.3, 1, 0.3]);

    return (
        <section ref={containerRef} className="py-10 md:py-16 bg-zinc-950 relative overflow-hidden">
            {/* Ambient Glow Effects */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 blur-[100px] rounded-full pointer-events-none" />

            <div className="max-w-5xl mx-auto px-6 text-center space-y-12 md:space-y-16 relative z-10">
                <motion.div
                    style={{ y: yTransform, opacity: opacityTransform }}
                    className="space-y-4"
                >
                    <h2 className="text-3xl md:text-6xl font-black uppercase tracking-tighter text-white">
                        {settings.magsafe_title.replace('Architecture.', '')} <span className="text-white italic">Architecture.</span>
                    </h2>
                    <p className="text-zinc-400 md:text-zinc-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                        {settings.magsafe_description}
                    </p>
                </motion.div>

                {/* Interactive MagSafe Visualization */}
                <div className="relative h-[400px] md:h-[600px] flex items-center justify-center">
                    <motion.div
                        animate={{ scale: [1, 1.05, 1], opacity: [0.2, 0.5, 0.2] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute w-[250px] h-[250px] md:w-[450px] md:h-[450px] border border-white/20 rounded-full"
                    />
                    <motion.div
                        animate={{ scale: [1, 1.1, 1], opacity: [0.1, 0.3, 0.1] }}
                        transition={{ duration: 4, repeat: Infinity, delay: 0.5, ease: "easeInOut" }}
                        className="absolute w-[350px] h-[350px] md:w-[600px] md:h-[600px] border border-white/10 rounded-full"
                    />
                    {/* MagSafe Core Glow */}
                    <motion.div
                        animate={{
                            opacity: [0.3, 0.7, 0.3],
                            scale: [0.95, 1.05, 0.95]
                        }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute w-[180px] h-[180px] md:w-[280px] md:h-[280px] bg-white/20 blur-[40px] rounded-full"
                    />

                    {/* Real MagSafe Shape */}
                    <motion.div
                        animate={{
                            filter: [
                                "drop-shadow(0 0 10px rgba(255,255,255,0.4))",
                                "drop-shadow(0 0 25px rgba(255,255,255,1))",
                                "drop-shadow(0 0 10px rgba(255,255,255,0.4))"
                            ]
                        }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                        className="relative z-20 flex items-center justify-center"
                    >
                        <div className="relative flex items-center justify-center">
                            <svg
                                viewBox="0 0 100 100"
                                className="w-56 h-56 md:w-[320px] md:h-[320px] text-white relative z-10"
                                fill="none"
                            >
                                <circle cx="50" cy="42" r="32" stroke="currentColor" strokeWidth="2.5" />
                                <path d="M50 82 L50 94" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                            </svg>
                            <Zap className="absolute w-16 h-16 md:w-24 md:h-24 text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.8)] z-20 top-[42%] left-[50%] -translate-x-1/2 -translate-y-1/2" />
                        </div>
                    </motion.div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                    {[
                        { title: "Carga Rápida", desc: "Optimizado para cargadores Qi y Apple MagSafe.", highlight: false },
                        { title: "Alineación", desc: "Imanes N52 de alto grado para un acople perfecto.", highlight: true },
                        { title: "Sin MagSafe", desc: "También ofrecemos la versión Slim para los puristas.", highlight: false }
                    ].map((feature, idx) => (
                        <motion.div
                            key={idx}
                            whileHover={{ scale: 1.02 }}
                            className={`p-6 md:p-8 border rounded-2xl space-y-3 transition-colors ${feature.highlight
                                ? 'border-white/20 bg-white/5 shadow-lg'
                                : 'border-white/5 bg-zinc-900/30'
                                }`}
                        >
                            <CheckCircle2 className={`w-6 h-6 mx-auto ${feature.highlight ? 'text-white' : 'text-zinc-500'}`} />
                            <h4 className="font-bold uppercase text-xs md:text-sm tracking-widest text-white">{feature.title}</h4>
                            <p className="text-[10px] md:text-xs text-zinc-400">{feature.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default TechSection;
